// Ring 1 — platformer-feel state machine. Implements CLIMB-01..04 plus
// CLIMB-07 (fast-fall, D-09) at the dopamine-saturated Celeste-family
// band per D-08..D-10.
//
// Source of truth: RESEARCH.md Pattern 5 (lines 738–807). All gameplay
// constants are imported from `src/data/tunables.ts` — per CLAUDE.md
// anti-pattern, NO bare gameplay numbers in this file.
//
// Key invariants this controller enforces:
//
//   - Coyote (T.COYOTE_MS) and jump-buffer (T.JUMP_BUFFER_MS) use ms
//     timestamps compared against `scene.time.now` and the `time` arg
//     to update(). NEVER frame counters (PITFALL #1, #3).
//
//   - dt is capped at T.MAX_DT_MS / 1000 to survive tab-blur RAF
//     backlogs (PITFALL #13). Companion to `pauseOnBlur: true`.
//
//   - Variable-jump cut on key release is a HARD CLAMP to MIN_JUMP_VELOCITY
//     when ascending — never `velocity.y *= 0.5` (anti-pattern #1).
//
//   - Asymmetric fall + fast-fall use PER-BODY gravity, not world.gravity.
//     world.gravity is zeroed in main.ts for exactly this reason — keeps
//     hazard physics in later phases isolated from player feel.
//
//   - Frame-rate independence: every velocity update is multiplied by
//     `dt` (seconds). RUN_ACCEL is in px/s², so RUN_ACCEL * dt yields a
//     px/s velocity delta per frame (CLIMB-04).
//
//   - Per-action juice (D-10): 1f y-scale 0.85 squash on jump start
//     baked into the entity, no plugins.
//
//   - One-way drop-through (CLIMB-03 second half): pressing Down while
//     grounded sets `dropThroughUntilMs` to `now + T.DROP_THROUGH_MS`.
//     The processCallback in RunScene's collider reads this field and
//     returns false during the window so the player passes through.

import Phaser from 'phaser';
import { TUNABLES as T } from '../data/tunables';

// Placeholder visual constants — NOT gameplay-feel constants. The body
// hitbox is a physics-shape parameter; the colors are visual. Real
// player sprite lands Phase 3 (VISUAL-03).
const BODY_W = 8;
const BODY_H = 14;
const PLACEHOLDER_SIZE = 12;
const PLACEHOLDER_FILL = 0xc9a14a; // amber rectangle (D-14)
const PLACEHOLDER_STROKE = 0x08050a;
const SQUASH_SCALE = 0.85; // D-10 visual juice (1f scale)
const SQUASH_DURATION_MS = 16; // single frame at 60fps — visual not feel

export class Player extends Phaser.Physics.Arcade.Sprite {
  static readonly TEXTURE_KEY = 'degenkong:player-placeholder';

  // Exposed for the one-way-platform processCallback in RunScene.
  // Set in tryDropThrough(); the collider reads it via the player ref
  // passed into its processCallback fourth argument (RESEARCH Pattern 7).
  dropThroughUntilMs: number = -Infinity;

  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd!: {
    W: Phaser.Input.Keyboard.Key;
    A: Phaser.Input.Keyboard.Key;
    S: Phaser.Input.Keyboard.Key;
    D: Phaser.Input.Keyboard.Key;
  };

  private lastGroundedAtMs = -Infinity;
  private jumpBufferedUntilMs = -Infinity;

  static ensureTexture(scene: Phaser.Scene): void {
    if (scene.textures.exists(Player.TEXTURE_KEY)) return;
    const g = scene.add.graphics({ x: 0, y: 0 });
    g.fillStyle(PLACEHOLDER_FILL, 1);
    g.fillRect(0, 0, PLACEHOLDER_SIZE, PLACEHOLDER_SIZE);
    g.lineStyle(1, PLACEHOLDER_STROKE, 1);
    g.strokeRect(0, 0, PLACEHOLDER_SIZE, PLACEHOLDER_SIZE);
    g.generateTexture(Player.TEXTURE_KEY, PLACEHOLDER_SIZE, PLACEHOLDER_SIZE);
    g.destroy();
  }

  constructor(scene: Phaser.Scene, x: number, y: number) {
    Player.ensureTexture(scene);
    super(scene, x, y, Player.TEXTURE_KEY);
    scene.add.existing(this);
    scene.physics.add.existing(this);

    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setSize(BODY_W, BODY_H);
    body.setMaxVelocity(T.MOVE_SPEED * 1.5, T.MAX_FALL_SPEED);
    body.setCollideWorldBounds(true);

    const kb = scene.input.keyboard;
    if (!kb) {
      throw new Error('Player requires scene.input.keyboard to be available');
    }

    this.cursors = kb.createCursorKeys();
    this.wasd = kb.addKeys('W,A,S,D') as typeof this.wasd;

    kb.on('keydown-SPACE', () => this.queueJump(scene.time.now));
    kb.on('keydown-W', () => this.queueJump(scene.time.now));
    kb.on('keyup-SPACE', () => this.maybeCutJump());
    kb.on('keyup-W', () => this.maybeCutJump());

    // Drop-through (CLIMB-03 second half) — RESEARCH Pattern 7 lines 911–916.
    kb.on('keydown-S', () => this.tryDropThrough(scene.time.now));
    kb.on('keydown-DOWN', () => this.tryDropThrough(scene.time.now));
  }

  private queueJump(now: number): void {
    this.jumpBufferedUntilMs = now + T.JUMP_BUFFER_MS;
  }

  // Variable-jump cut: HARD CLAMP to MIN_JUMP_VELOCITY when ascending.
  // Never `velocity.y *= 0.5` — produces unstable felt jump heights when
  // frame timing varies (platformer-feel anti-pattern #1).
  private maybeCutJump(): void {
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (body.velocity.y < T.MIN_JUMP_VELOCITY) {
      body.setVelocityY(T.MIN_JUMP_VELOCITY);
    }
  }

  private tryDropThrough(now: number): void {
    const body = this.body as Phaser.Physics.Arcade.Body;
    const grounded = body.blocked.down || body.touching.down;
    if (grounded) {
      this.dropThroughUntilMs = now + T.DROP_THROUGH_MS;
      body.setVelocityY(T.DROP_THROUGH_KICK);
    }
  }

  override update(time: number, delta: number): void {
    const body = this.body as Phaser.Physics.Arcade.Body;

    // PITFALL #13 — cap dt so a tab-blur RAF backlog doesn't teleport
    // the player. Combined with `pauseOnBlur: true` in main.ts.
    const dt = Math.min(delta, T.MAX_DT_MS) / 1000;

    const grounded = body.blocked.down || body.touching.down;
    if (grounded) this.lastGroundedAtMs = time;

    // Horizontal — accel-based with air-control multiplier (D-08).
    // RUN_ACCEL is px/s², dt is seconds, so RUN_ACCEL * dt is the
    // per-frame velocity delta in px/s. CLIMB-04 frame-rate independence.
    const left = this.cursors.left.isDown || this.wasd.A.isDown;
    const right = this.cursors.right.isDown || this.wasd.D.isDown;
    const accelMult = grounded ? 1 : T.AIR_CONTROL_RATIO;
    if (left) {
      body.setVelocityX(
        Math.max(body.velocity.x - T.RUN_ACCEL * accelMult * dt, -T.MOVE_SPEED),
      );
    } else if (right) {
      body.setVelocityX(
        Math.min(body.velocity.x + T.RUN_ACCEL * accelMult * dt, T.MOVE_SPEED),
      );
    } else if (grounded) {
      // Instant stop on ground — input crispness (anti-pattern #2: don't lerp).
      body.setVelocityX(0);
    }

    // Coyote + jump-buffer combined consume.
    const canCoyote = time - this.lastGroundedAtMs <= T.COYOTE_MS;
    const wantsJump = time <= this.jumpBufferedUntilMs;
    if (wantsJump && (grounded || canCoyote)) {
      body.setVelocityY(T.JUMP_VELOCITY);
      this.jumpBufferedUntilMs = -Infinity; // consume buffer
      this.lastGroundedAtMs = -Infinity; // consume coyote
      this.juiceJumpStart();
    }

    // Asymmetric gravity (CLIMB-02) + fast-fall (CLIMB-07 / D-09).
    // velocity.y > 0 = descending in Phaser's y-down coordinate system.
    const wantsFastFall =
      !grounded && (this.cursors.down.isDown || this.wasd.S.isDown);
    let gMult = body.velocity.y > 0 ? T.FALL_GRAVITY_MULT : 1;
    if (wantsFastFall) gMult = T.FAST_FALL_GRAVITY_MULT;
    body.setGravityY(T.GRAVITY * gMult);
  }

  // D-10: 1f y-scale 0.85 squash on jump start. Purely visual; lives in
  // the entity (no plugin) per the constraint.
  private juiceJumpStart(): void {
    this.scene.tweens.add({
      targets: this,
      scaleY: SQUASH_SCALE,
      duration: SQUASH_DURATION_MS,
      yoyo: true,
    });
  }
}
